<template>
  <div class="contact-block">
    <div v-if="isSending" class="contact-sending">
      <div class="loading-spinner" />
      <p>Sending your message...</p>
    </div>
    <div v-else-if="isError" class="contact-feedback-state">
      <FeedbackMessage type="error" message="Something went wrong. Your message could not be sent. Please try again later." />
      <button class="contact-btn contact-btn-secondary" title="Try again" aria-label="Try again" @click="isError = false">Try again</button>
    </div>
    <div v-else-if="!isSent" class="contact-form-container">
      <form @submit.prevent="send()">
        <h2 v-if="heading" class="contact-heading">
          {{ heading }}
        </h2>
        <div class="contact-row">
          <label for="contactName">Name</label>
          <input id="contactName" v-model="client.name" type="text" name="contactName" maxlength="200">
          <transition name="fade">
            <FeedbackMessage v-if="errors.name" type="error" :message="errors.name" />
          </transition>
        </div>
        <div class="contact-row">
          <label for="contactEmail">Email</label>
          <input id="contactEmail" v-model="client.email" type="email" name="contactEmail" maxlength="254">
          <transition name="fade">
            <FeedbackMessage v-if="errors.email" type="error" :message="errors.email" />
          </transition>
        </div>
        <div v-if="showPhone" class="contact-row">
          <label for="contactPhone">Phone</label>
          <input id="contactPhone" v-model="client.phone" type="text" name="contactPhone" maxlength="50">
        </div>
        <div v-if="showPredefinedOptions" class="contact-row">
          <label for="contactSubject">Subject</label>
          <select id="contactSubject" v-model="client.subject" name="contactSubject">
            <option v-for="(option, index) in predefinedSubjectOptions" :key="index">
              {{ option }}
            </option>
          </select>
          <input
            v-if="client.subject === 'Something else'"
            id="contactSubjectCustom"
            v-model="client.customSubject"
            type="text"
            maxlength="200"
            placeholder="please specify"
            class="custom-subject-input"
          >
        </div>
        <div v-else class="contact-row">
          <label for="contactSubject">Subject</label>
          <input id="contactSubject" v-model="client.subject" type="text" name="contactSubject" maxlength="200">
        </div>
        <div class="contact-row">
          <label for="contactMessage">Message</label>
          <textarea id="contactMessage" v-model="client.message" name="contactMessage" maxlength="5000" />
          <transition name="fade">
            <FeedbackMessage v-if="errors.message" type="error" :message="errors.message" />
          </transition>
        </div>
        <div class="hp-field" aria-hidden="true">
          <label for="contactHp">Leave this empty</label>
          <input id="contactHp" v-model="website" type="text" name="hp_extra" tabindex="-1" autocomplete="off">
        </div>
        <div class="contact-row">
          <captcha class="contact-rowInput" @total-created="getAntiSpamTotal" />
          <input id="contactControlInput" v-model.number="controlInputNumber" type="number" name="contactControlInput">
          <transition name="fade">
            <FeedbackMessage v-if="errors.antispam" type="error" :message="errors.antispam" />
          </transition>
        </div>
        <div class="button-row">
          <button type="reset" class="contact-btn contact-btn-secondary" title="Clear" aria-label="Clear" @click="reset">Clear</button>
          <button type="submit" class="contact-btn contact-btn-primary" :title="submitButtonText" :aria-label="submitButtonText">{{ submitButtonText }}</button>
        </div>
      </form>
    </div>
    <transition v-else name="fade">
      <div class="contact-feedback-state">
        <FeedbackMessage type="success" :message="successMessage" />
        <button class="contact-btn contact-btn-secondary" title="Go back" aria-label="Go back" @click="isSent = false">Go back</button>
      </div>
    </transition>
  </div>
</template>

<script>
import Captcha from '@/components/Captcha.vue'
import FeedbackMessage from '@/components/FeedbackMessage.vue'
import { submitInboxMessage } from '@/utils/submitInboxMessage'
import { getTenantUid } from '@/utils/tenantUtils'
import { store } from '@/store'

export default {
  name: 'ContactBlock',
  components: {
    captcha: Captcha,
    FeedbackMessage
  },
  props: {
    data: {
      type: Object,
      default: () => ({})
    }
  },
  data() {
    return {
      controlInputNumber: null,
      antiSpamNumber: null,
      errors: this.getFreshErrors(),
      client: this.getNewClient(),
      website: '',
      loadedAt: Date.now(),
      isSent: false,
      isSending: false,
      isError: false
    }
  },
  computed: {
    heading() {
      return this.data.heading || ''
    },
    successMessage() {
      return this.data.successMessage || 'Thank you! Your message has been sent!'
    },
    showPhone() {
      return this.data.showPhone || false
    },
    showPredefinedOptions() {
      return this.data.showPredefinedOptions || false
    },
    predefinedSubjectOptions() {
      return [
        'Choose a subject',
        'About your art',
        'Exhibition or event',
        'Collaboration',
        'Something else'
      ]
    },
    submitButtonText() {
      return this.data.submitButtonText || 'Send'
    },
    // The page the form sits on, shown as "Source" in the Inbox
    source() {
      const slug = this.$route?.params?.slug
      const page = (store.customPages || []).find(p => slug ? p.slug === slug : p.id === '_home')
      return page?.title || slug || 'Home'
    }
  },
  mounted() {
    this.reset()
  },
  methods: {
    getAntiSpamTotal(val) {
      this.antiSpamNumber = val
    },
    getFreshErrors() {
      return {
        email: null,
        antispam: null,
        name: null,
        message: null
      }
    },
    getNewClient() {
      return {
        name: null,
        email: null,
        phone: null,
        message: null,
        subject: this.showPredefinedOptions ? 'Choose a subject' : '',
        customSubject: ''
      }
    },
    reset() {
      this.client = this.getNewClient()
      this.errors = this.getFreshErrors()
    },
    send() {
      this.isError = false
      this.errors = this.getFreshErrors()
      if (!this.client.name) {
        this.errors.name = 'Name is required'
      }
      if (!this.client.email) {
        this.errors.email = 'Email is required'
      }
      if (!this.client.message) {
        this.errors.message = 'Message is required'
      }
      if (this.controlInputNumber !== this.antiSpamNumber) {
        this.errors.antispam = 'Incorrect answer, please try again'
      }
      if (!this.controlInputNumber) {
        this.errors.antispam = 'Please enter the sum of the numbers above'
      }
      if (this.client.email && !this.validEmail(this.client.email)) {
        this.errors.email = 'Please enter a valid email address'
      }

      const formHasNoErrors = !Object.values(this.errors).some(x => (x !== null && x !== ''))

      if (!formHasNoErrors) {
        return false
      }

      this.isSending = true

      const tenantUid = getTenantUid()
      const date = new Date()
      const toSend = {
        uid: tenantUid,
        kind: 'contact',
        name: this.client.name,
        email: this.client.email,
        message: this.client.message,
        formattedDate: date.toLocaleDateString('ro-RO') + ' ' + date.toLocaleTimeString('ro-RO'),
        website: this.website,
        elapsedMs: Date.now() - this.loadedAt
      }

      // Include phone if shown
      if (this.showPhone) {
        toSend.phone = this.client.phone || 'Not filled in'
      }

      if (this.source) {
        toSend.source = this.source
      }

      // Include subject (always shown)
      if (this.showPredefinedOptions) {
        if (this.client.subject === 'Something else') {
          toSend.subject = this.client.customSubject || 'Not filled in'
        } else if (this.client.subject === 'Choose a subject') {
          toSend.subject = 'Not selected'
        } else {
          toSend.subject = this.client.subject || 'Not filled in'
        }
      } else {
        toSend.subject = this.client.subject || 'Not filled in'
      }

      if (!tenantUid) {
        this.isSending = false
        this.isError = true
        return
      }
      submitInboxMessage(toSend)
        .then(() => {
          this.isSending = false
          this.isSent = true
        })
        .catch(err => {
          console.error('Failed to save contact:', err)
          this.isSending = false
          this.isError = true
        })
    }
  }
}
</script>

<style scoped>
.contact-heading {
  text-align: center;
  font-size: 1.6em;
  margin: 0 0 24px;
}

.contact-row {
  margin-bottom: 16px;

  label,
  & :deep(.captcha) {
    display: block;
    font-size: var(--font-xs);
    margin-bottom: 6px;
    padding-left: 12px;
  }

  input,
  select,
  textarea {
    width: 100%;
    padding: 10px 12px;
    font-size: 15px;
    border: 1px solid var(--color-light-gray);
    border-radius: var(--radius);
    box-sizing: border-box;
    font-family: inherit;

    &:focus {
      outline: none;
      border-color: color-mix(in srgb, var(--color-text) 50%, var(--color-bg));
    }
  }

  textarea {
    min-height: 120px;
    resize: vertical;
  }

  select {
    appearance: auto;
  }

  .custom-subject-input {
    margin-top: 8px;
  }

}

.button-row {
  display: flex;
  justify-content: center;
  gap: 12px;
  margin-top: 24px;
}

.contact-btn {
  padding: 10px 28px;
  border-radius: var(--radius);
  font-family: var(--font-primary);
  font-size: var(--font-sm);
  cursor: pointer;
  border: none;
  transition: background-color var(--transition-base), color var(--transition-base);
}

.contact-btn-primary {
  background-color: var(--color-accent);
  color: var(--color-accent-text);

  &:hover {
    background-color: var(--color-accent-hover);
  }
}

.contact-btn-secondary {
  background-color: transparent;
  color: var(--color-outline-button);
  border: 2px solid var(--color-outline-button);

  &:hover {
    background-color: var(--color-outline-button);
    color: var(--color-outline-button-text);
  }
}

.contact-sending {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px 0;

  p {
    margin-top: 16px;
    color: color-mix(in srgb, var(--color-text) 60%, var(--color-bg));
    font-size: 15px;
  }
}

.loading-spinner {
  width: 48px;
  height: 48px;
}

.contact-feedback-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 20px 0;
}
</style>
